<template>
  <div class="notes-container">
    <!-- Mobile Version -->
    <div v-if="isMobile()">
      <!-- Notes Header -->
      <div class="notes-header" id="notesHeader">NOTES</div>

      <!-- Create New Note Button -->
      <div class="create-note">
        <button
          id="createNewNote"
          class="create-note-button"
          @click="showNote(patient.id)"
        >
          <span class="plus-symbol">+</span> Create New Note
        </button>
      </div>

      <!-- Timer and Stop Timer -->
      <div class="timer-section">
        <p id="timer">{{ displayTime }}</p>
        <button id="stopTimer" class="stop-timer-button" @click="toggleTimer">
          {{ isTimed ? "Stop Timer" : "Start Timer" }}
        </button>
      </div>

      <!-- Note Type Dropdown -->
      <div class="note-type-section">
        <select
          id="noteTypeDropdown"
          v-model="selectedNoteType"
          @change="handleFilterChange"
        >
          <option value="" disabled>Select note</option>
          <option v-for="type in uniqueAccountTypes" :key="type" :value="type">
            {{ formatLabel(type) }}
          </option>
          <option value="show-all">Show All</option>
        </select>
      </div>

      <!-- Note Category Dropdown -->
      <div class="note-type-section">
        <select
          id="noteCategoryDropdown"
          v-model="selectedNoteCategory"
          @change="handleFilterChange"
        >
          <option value="" disabled>Select type</option>
          <option v-for="category in uniqueTypes" :key="category" :value="category">
            {{ formatTypeLabel(category) }}
          </option>
          <option value="show-all">Show All</option>
        </select>
      </div>

      <!-- Date Range Dropdown -->
      <div class="note-type-section">
        <select
          id="dateRangeDropdown"
          v-model="selectedDateRange"
          @change="handleFilterChange"
        >
          <option value="" disabled>Select dates</option>
          <option value="lastWeek">Last week</option>
          <option value="last2Weeks">Last 2 weeks</option>
          <option value="last30Days">Last 30 days</option>
          <option value="last90Days">Last 90 days</option>
          <option value="last365Days">Last 365 days</option>
          <option value="all">All</option>
        </select>
      </div>

      <!-- OR Text -->
      <p class="or-text">OR</p>

      <!-- Custom Date Picker (Flatpickr) -->
      <div class="date-range-section">
        <flat-pickr
          id="datePicker"
          v-model="customDateRange"
          :config="flatpickrConfig"
          placeholder="Select a date or range"
          @on-change="handleCustomDateChange"
        ></flat-pickr>
      </div>

      <!-- Notes Display Section -->
      <div id="notes-container" class="notes-display">
        <!-- Date Buttons -->
        <div v-for="date in filteredDates" :key="date">
          <button class="date-button" @click="toggleNoteForDate(date)">
            {{ formatDisplayDate(date) }}
          </button>
          <!-- Note Content -->
          <div v-if="expandedDate === date" class="note-content">
            <!-- Note Tabs -->
            <div class="note-tabs-wrapper">
              <div class="note-tabs">
                <button
                  v-for="(writer, index) in writers"
                  :key="index"
                  class="tab"
                  :class="{
                    'active-tab': activeTab === index,
                    'inactive-tab': activeTab !== index,
                  }"
                  @click="selectWriterTab(index)"
                >
                  {{ writer }}
                </button>
              </div>
            </div>
            <!-- Note Content Inner -->
            <div class="note-content-inner">
              <div v-if="currentNote">
                <h4 v-if="currentNote.startTime && currentNote.endTime">
                  {{ formatTimeRange(currentNote.startTime, currentNote.endTime) }}
                </h4>
                <h4 v-else-if="currentNote.startTime || currentNote.endTime">
                  {{ formatPartialTime(currentNote.startTime, currentNote.endTime) }}
                </h4>
                <h4 v-else>No time specified</h4>
                <p v-html="currentNote.pnotes"></p>
                <note-attachment-list :note="currentNote" />
              </div>
            </div>
            <!-- Pagination Fixed at Bottom -->
            <div class="pagination" v-if="totalPages > 1">
              <button class="prev-page" :disabled="currentPage === 1" @click="prevPage">
                &#8249;
                <!-- Left Arrow -->
              </button>
              <span class="page-indicator">{{ currentPage }} / {{ totalPages }}</span>
              <button
                class="next-page"
                :disabled="currentPage === totalPages"
                @click="nextPage"
              >
                &#8250;
                <!-- Right Arrow -->
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Note Popup Component -->
      <note-popup
        v-if="patientData"
        :time="noteTime"
        ref="notePopup"
        :type="notePopupDefaultType"
        class="note__popup"
        :patient="patientData"
        :id="'note__popup' + patientPopupKey"
        v-show="activeNotePopupId === patient.id"
        @note-saved="handleNoteSaved"
        @closeNote="handleNoteClosed"
      />
    </div>

    <!-- Desktop Version -->
    <div v-else class="notes-container-desktop">
      <!-- Notes Header -->
      <div class="notes-header-desktop">NOTES</div>

      <!-- Create New Note Button -->
      <div class="create-note-desktop">
        <button class="create-note-button-desktop" @click="showNote(patient.id)">
          <span class="plus-symbol-desktop">+</span> Create New Note
        </button>
      </div>

      <!-- Timer and Stop Timer -->
      <div class="timer-section-desktop">
        <span id="timer-desktop">{{ displayTime }}</span>
        <button
          id="stopTimerDesktop"
          class="stop-timer-button-desktop"
          @click="toggleTimer"
        >
          {{ isTimed ? "Stop Timer" : "Start Timer" }}
        </button>
      </div>

      <!-- Dropdown section for note type and category -->
      <div class="dropdown-container-desktop">
        <div class="note-type-section-desktop">
          <select
            id="noteTypeDropdownDesktop"
            v-model="selectedNoteType"
            @change="handleFilterChange"
          >
            <option value="">Select note</option>
            <option v-for="type in uniqueAccountTypes" :key="type" :value="type">
              {{ formatLabel(type) }}
            </option>
            <option value="show-all">Show All</option>
          </select>
        </div>

        <div class="note-type-section-desktop">
          <select
            id="noteCategoryDropdownDesktop"
            v-model="selectedNoteCategory"
            @change="handleFilterChange"
          >
            <option value="">Select type</option>
            <option v-for="category in uniqueTypes" :key="category" :value="category">
              {{ formatTypeLabel(category) }}
            </option>
            <option value="show-all">Show All</option>
          </select>
        </div>
      </div>

      <!-- Date range section aligned with "OR" text -->
      <div class="date-range-row">
        <div class="note-type-section-desktop">
          <select
            id="dateRangeDropdownDesktop"
            v-model="selectedDateRange"
            @change="handleFilterChange"
          >
            <option value="">Select dates</option>
            <option value="lastWeek">Last week</option>
            <option value="last2Weeks">Last 2 weeks</option>
            <option value="last30Days">Last 30 days</option>
            <option value="last90Days">Last 90 days</option>
            <option value="last365Days">Last 365 days</option>
            <option value="all">All</option>
          </select>
        </div>

        <div class="or-text-desktop">OR</div>

        <div class="date-range-section-desktop">
          <flat-pickr
            id="datePickerDesktop"
            v-model="customDateRange"
            :config="flatpickrConfig"
            placeholder="Select date range"
            @on-change="handleCustomDateChange"
          ></flat-pickr>
        </div>
      </div>

      <!-- Notes Display Section -->
      <div id="notes-container-desktop" class="notes-display-desktop">
        <!-- Date Buttons -->
        <div v-for="date in filteredDates" :key="date">
          <button
            class="date-button-desktop"
            @click="toggleNoteForDate(date)"
            :class="{ 'active-date-button-desktop': expandedDate === date }"
          >
            {{ formatDisplayDate(date) }}
          </button>
          <!-- Note Content -->
          <div v-if="expandedDate === date" class="note-content-desktop">
            <!-- Note Tabs -->
            <div class="note-tabs-wrapper-desktop">
              <div class="note-tabs-desktop">
                <button
                  v-for="(writer, index) in writers"
                  :key="index"
                  class="tab-desktop"
                  :class="{
                    'active-tab-desktop': activeTab === index,
                    'inactive-tab-desktop': activeTab !== index,
                  }"
                  @click="selectWriterTab(index)"
                >
                  {{ writer }}
                </button>
              </div>
            </div>
            <!-- Note Content Inner -->
            <div class="note-content-inner-desktop">
              <div v-if="currentNote">
                <h4 v-if="currentNote.startTime && currentNote.endTime">
                  {{ formatTimeRange(currentNote.startTime, currentNote.endTime) }}
                </h4>
                <h4 v-else-if="currentNote.startTime || currentNote.endTime">
                  {{ formatPartialTime(currentNote.startTime, currentNote.endTime) }}
                </h4>
                <h4 v-else>No time specified</h4>
                <p v-html="currentNote.pnotes"></p>
                <note-attachment-list :note="currentNote" />
              </div>
            </div>
            <!-- Resize Handle -->
            <div
              class="resize-handle-desktop"
              @mousedown="
                startResizing($event, $event.target.closest('.note-content-desktop'))
              "
            >
              <div class="resize-arrow"></div>
            </div>
            <!-- Pagination -->
            <div class="pagination-desktop" v-if="totalPages > 1">
              <button
                class="prev-page-desktop"
                :disabled="currentPage === 1"
                @click="prevPage"
              >
                &#8249;
                <!-- Left Arrow -->
              </button>
              <span class="page-indicator-desktop"
                >{{ currentPage }} / {{ totalPages }}</span
              >
              <button
                class="next-page-desktop"
                :disabled="currentPage === totalPages"
                @click="nextPage"
              >
                &#8250;
                <!-- Right Arrow -->
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Note Popup Component -->
      <note-popup
        v-if="patientData"
        :time="noteTime"
        ref="notePopup"
        :type="notePopupDefaultType"
        class="note__popup"
        :patient="patientData"
        :id="'note__popup' + patientPopupKey"
        v-show="activeNotePopupId === patient.id"
        @note-saved="handleNoteSaved"
        @closeNote="handleNoteClosed"
      />
    </div>
  </div>
</template>

<script>
import { CURR_API, OTHER_API } from "@/constants/apis";
import { mapGetters, mapActions } from "vuex";
import axios from "axios";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import NotePopup from "@/components/private-layouts/NotePopup.vue";
import NoteAttachmentList from "@/components/includes/attachments/NoteAttachmentList.vue";
import { usePrivilegesStore } from "@/stores/privileges";
import { PRIVILEGES } from "@/constants/privileges";
import { assertPrivilegeOrAlert } from '@/utils/privilegeGate';
import { noteTypeForAccountType } from "@/constants/noteTypes";

export default {
  name: "NotesPage",
  components: {
    flatPickr,
    NotePopup,
    NoteAttachmentList,
  },
  data() {
    return {
      // vue3 modifications
      patientData: null,
      activeNotePopupId: null,
      // Timer States
      noteTime: "",
      isTimed: false,
      runningTime: 0,
      interval: null,

      // Selected Values
      selectedNoteType: "",
      selectedNoteCategory: "",
      selectedDateRange: "",
      customDateRange: null,
      startDate: null,
      endDate: null,

      // Date Picker Selected Dates
      selectedDates: [],

      // Notes Data
      notes: [], // Array to store all fetched notes
      notesByDate: {}, // Notes grouped by endDt

      writers: [],
      currentNote: null,
      currentPage: 1,
      totalPages: 1,
      activeTab: 0,
      expandedDate: null,

      // Unique Options for Dropdowns
      uniqueAccountTypes: [],
      uniqueTypes: [],

      // Flatpickr Configuration
      flatpickrConfig: {
        dateFormat: "Y-m-d",
        mode: "range",
        altInput: true,
        altFormat: "F j, Y",
      },
    };
  },
  computed: {
    ...mapGetters("login", {
      allData: "getAllData",
    }),
    patient: function () {
      return this.allData.patient;
    },
    /** Initial NotePopup kind from logged-in user's account_type. */
    notePopupDefaultType() {
      const accountType =
        this.allData?.manager?.accounttype ||
        this.allData?.user?.accounttype ||
        this.allData?.patient?.accounttype ||
        "";
      return noteTypeForAccountType(accountType);
    },
    patientPopupKey() {
      return this.patientData?.patient_id || this.patientData?.psersonalID || this.patientData?.id || this.patient?.externId || "";
    },
    // Display time in HH:MM:SS format
    displayTime() {
      let time = this.runningTime;
      let hours = Math.floor(time / 3600);
      let minutes = Math.floor((time % 3600) / 60);
      let seconds = time % 60;
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
        2,
        "0"
      )}:${String(seconds).padStart(2, "0")}`;
    },
    filteredDates() {
      // Sort dates in descending order
      return Object.keys(this.notesByDate).sort((a, b) => {
        const dateA = this.parseLocalDate(a);
        const dateB = this.parseLocalDate(b);
        if (!dateA || !dateB) return 0;
        return dateB - dateA;
      });
    },
  },
  mounted() {
    if (usePrivilegesStore().effectiveGranted(PRIVILEGES.APPLICATION_TIMER)) {
      this.startTimer();
    }
    this.getNotes(); // Fetch notes when component is mounted
    this.getPatientData()
  },
  methods: {
    // isMobile Function
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    },
    async getPatientData(){
      let content = {
        GetPatientInfo: {
          API_Meth: "GetCurrentPatientData",
          accountNumber: this.allData.patient.externId2,
          pid: this.allData.patient.externId
        }
      }
      await axios.post(CURR_API + "/tswebhook.php", content, {
        headers: { "Content-Type": "application/json;"},
      })
      .then((res) => {
        console.log(res)
        if (res.data.patientInfo){
          this.patientData = res.data.patientInfo[0]
        }
      })
      .catch((err) => {
        console.log(err)
      })
    },
    // Start Timer
    startTimer() {
      if (!this.isTimed) {
        this.interval = setInterval(() => {
          this.runningTime += 1;
        }, 1000);
        this.isTimed = true;
      }
    },

    // Stop Timer
    stopTimer() {
      if (this.isTimed) {
        clearInterval(this.interval);
        this.isTimed = false;
      }
    },

    // Toggle Timer
    toggleTimer() {
      if (this.isTimed) {
        this.stopTimer();
      } else {
        if (!assertPrivilegeOrAlert(
          PRIVILEGES.APPLICATION_TIMER,
          "You do not have permission to use the application timer."
        )) {
          return;
        }
        this.startTimer();
      }
    },

    // Show Note Popup
    showNote(id) {
      if (!assertPrivilegeOrAlert(
        PRIVILEGES.WRITE_NOTES,
        "You do not have permission to write notes."
      )) {
        return;
      }
      this.activeNotePopupId = id
      this.isTimed = false;
      clearInterval(this.interval);
      if (this.$refs.notePopup) {
        this.$refs.notePopup.startTimer();
      }
      let date = new Date();
      let tmpTime = date.toLocaleTimeString("en-US", { hour12: false }).slice(0, -3);
      this.noteTime = tmpTime.length == 7 ? `0${tmpTime}` : tmpTime;
      this.$nextTick(() => {
        const noteElement = document.getElementById("note__popup" + (this.patientData?.patient_id || this.patientData?.psersonalID || id));
        if (noteElement) {
          noteElement.style.display = "flex";
        }
      });
    },

    // Handle Note Saved Event
    handleNoteSaved() {
      this.getNotes();
      this.activeNotePopupId = null;
    },

    // Handle Note Closed Event (when user cancels)
    handleNoteClosed() {
      this.activeNotePopupId = null;
    },

    // Handle all filter changes
    handleFilterChange() {
      if (this.selectedDateRange != "") this.customDateRange = null;
      this.expandedDate = null;
      this.currentPage = 1;
      this.activeTab = 0;
      this.applyFilters();
    },

    // Apply Filters based on selected dropdowns
    applyFilters() {
      // Start with all notes
      let tempNotes = [...this.notes];

      // Filter by accountType (selectedNoteType)
      if (this.selectedNoteType && this.selectedNoteType !== "show-all") {
        tempNotes = tempNotes.filter(
          (note) => note.accountType === this.selectedNoteType
        );
      }

      // Filter by type (selectedNoteCategory)
      if (this.selectedNoteCategory && this.selectedNoteCategory !== "show-all") {
        tempNotes = tempNotes.filter((note) => note.type === this.selectedNoteCategory);
      }

      // Filter by date range if selected
      if (this.selectedDateRange) {
        const today = new Date();
        let startDate;

        switch (this.selectedDateRange) {
          case "lastWeek":
            startDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case "last2Weeks":
            startDate = new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000);
            break;
          case "last30Days":
            startDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          case "last90Days":
            startDate = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
            break;
          case "last365Days":
            startDate = new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000);
            break;
          case "all":
            startDate = new Date(today.getTime() - 3650 * 24 * 60 * 60 * 1000); // 10 years
            break;
          default:
            startDate = today;
            break;
        }

        tempNotes = tempNotes.filter((note) => {
          const noteDate = this.parseLocalDate(note.endDt);
          return noteDate >= startDate && noteDate <= today;
        });
      }

      // If custom date range is selected via flatpickr
      if (this.customDateRange) {
        // const [start, end] = this.customDateRange;
        tempNotes = tempNotes.filter((note) => {
          const noteDate = this.parseLocalDate(note.endDt);
          const startDateObj = this.parseLocalDate(this.startDate);
          const endDateObj = this.parseLocalDate(this.endDate);
          if (!noteDate || !startDateObj || !endDateObj) return false;
          return noteDate >= startDateObj && noteDate <= endDateObj;
        });
      }

      // Group notes by endDt
      this.notesByDate = {};
      tempNotes.forEach((note) => {
        if (!this.notesByDate[note.endDt]) {
          this.notesByDate[note.endDt] = []
          // this.$set(this.notesByDate, note.endDt, []);
        }
        this.notesByDate[note.endDt].push(note);
      });
    },

    // Handle Custom Date Change (Flatpickr)
    handleCustomDateChange(selectedDatesArray) {
      if (!this.customDateRange) {
        this.startDate = null;
        this.endDate = null;
        return;
      }
      this.selectedDateRange = "";
      if (selectedDatesArray.length === 1) {
        this.startDate = selectedDatesArray[0];
        this.endDate = selectedDatesArray[0];
        // this.customDateRange = [selectedDatesArray[0], selectedDatesArray[0]];
      } else if (selectedDatesArray.length === 2) {
        this.startDate = selectedDatesArray[0];
        this.endDate = selectedDatesArray[1];
        // this.customDateRange = selectedDatesArray;
      } else {
        this.startDate = null;
        this.endDate = null;
        // this.customDateRange = null;
      }
      this.handleFilterChange(); // Apply filters after selecting custom dates
    },

    // Fetch Notes from Backend
    async getNotes() {
      let content = {
        Note: {
          API_Meth: "GetAllNotes",
          // Adjusted to use the correct patient ID field
          patientID: this.allData.patient.externId,
        },
      };
      try {
        const res = await axios.post(`${CURR_API}/tswebhook.php`, content, {
          headers: { "Content-Type": "application/json;" },
        });
        if (res.data.data) {
          this.notes = res.data.data.map((note) => ({
            ...note,
            open: false,
          }));
          // Populate unique dropdown options
          this.populateUniqueOptions();
          // After fetching, apply current filters
          this.applyFilters();
        } else {
          this.notes = [];
          this.notesByDate = {};
        }
      } catch (error) {
        console.error("Error fetching notes:", error);
        this.notes = [];
        this.notesByDate = {};
      }
    },

    // Populate uniqueAccountTypes and uniqueTypes for dropdowns
    populateUniqueOptions() {
      const accountTypes = new Set();
      const types = new Set();
      this.notes.forEach((note) => {
        if (note.accountType) accountTypes.add(note.accountType);
        if (note.type) types.add(note.type);
      });
      this.uniqueAccountTypes = Array.from(accountTypes);
      this.uniqueTypes = Array.from(types);
    },

    // Toggle Note for Date
    toggleNoteForDate(date) {
      if (this.expandedDate === date) {
        this.expandedDate = null;
      } else {
        this.expandedDate = date;
        this.currentPage = 1;
        this.activeTab = 0;
        this.writers = this.getWritersForDate(date);
        if (this.writers.length > 0) {
          this.selectWriterTab(0);
        }
      }
    },

    // Get unique writers for a specific date
    getWritersForDate(date) {
      const notes = this.notesByDate[date];
      const writerSet = new Set(notes.map((note) => note.name));
      return Array.from(writerSet);
    },

    // Select Writer Tab
    selectWriterTab(index) {
      this.activeTab = index;
      this.currentPage = 1;
      const writer = this.writers[index];
      const writerNotes = this.getNotesForWriter(writer);
      this.totalPages = writerNotes.length;
      this.displayNotesForWriter(writer, this.currentPage);
    },

    // Get notes for a specific writer on the expandedDate
    getNotesForWriter(writer) {
      if (!this.expandedDate) return [];
      return this.notesByDate[this.expandedDate].filter((note) => note.name === writer);
    },

    // Display Notes for Writer
    displayNotesForWriter(writer, page) {
      const writerNotes = this.getNotesForWriter(writer);
      if (writerNotes.length === 0) {
        this.currentNote = null;
        return;
      }
      this.currentNote = writerNotes[page - 1];
    },

    // Pagination Methods
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        const writer = this.writers[this.activeTab];
        this.displayNotesForWriter(writer, this.currentPage);
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        const writer = this.writers[this.activeTab];
        this.displayNotesForWriter(writer, this.currentPage);
      }
    },

    // Parse date string to local Date object (avoids timezone issues)
    parseLocalDate(dateString) {
      if (!dateString) return null;
      const dateParts = dateString.split("-");
      if (dateParts.length !== 3) return new Date(dateString);
      return new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
    },

    // Format Date for Display (e.g., "April 27, 2024")
    formatDisplayDate(date) {
      const options = { year: "numeric", month: "long", day: "numeric" };
      const localDate = this.parseLocalDate(date);
      return localDate ? localDate.toLocaleDateString(undefined, options) : date;
    },

    // Format Time Range for Display in AM/PM format
    formatTimeRange(startTime, endTime) {
      const formattedStart = this.formatToAMPM(startTime);
      const formattedEnd = this.formatToAMPM(endTime);
      return `${formattedStart} - ${formattedEnd}`;
    },

    // Format Partial Time Range
    formatPartialTime(startTime, endTime) {
      if (startTime && endTime) {
        return this.formatTimeRange(startTime, endTime);
      } else if (startTime) {
        return `${this.formatToAMPM(startTime)} - `;
      } else if (endTime) {
        return ` - ${this.formatToAMPM(endTime)}`;
      } else {
        return "No time specified";
      }
    },

    // Convert 24-hour time to 12-hour AM/PM format
    formatToAMPM(time) {
      if (!time) return "";
      let [hour, minute] = time.split(":").map(Number);
      const ampm = hour >= 12 ? "PM" : "AM";
      hour = hour % 12;
      hour = hour ? hour : 12; // the hour '0' should be '12'
      return `${hour}:${minute.toString().padStart(2, "0")} ${ampm}`;
    },

    // Format Label (capitalize and replace hyphens with spaces)
    formatLabel(value) {
      return value
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },

    // Format Type Label (special handling for CPT codes)
    formatTypeLabel(value) {
      if (value === '9945X' || value === '9898X') {
        return `Vitals - ${value}`;
      }
      return this.formatLabel(value);
    },

    // Start Resizing Note Content
    startResizing(event, noteContentElement) {
      const startY = event.clientY;
      const startHeight = noteContentElement.offsetHeight;

      const onMouseMove = (e) => {
        const dy = e.clientY - startY;
        const newHeight = startHeight + dy;
        if (newHeight > 100 && newHeight < 600) {
          noteContentElement.style.height = `${newHeight}px`;
        }
      };

      const onMouseUp = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
  },
};
</script>

<style scoped>
/* Mobile Styles */

/* Keep the notes page from forcing horizontal page scroll on phones. */
.notes-container {
  width: 100%;
  max-width: 100%;
  margin: 12px 0;
  padding: 16px;
  background-color: #ffffff;
  border-radius: 8px;
  box-sizing: border-box;
  overflow-x: hidden;
}

/* Notes Header */
.notes-header {
  background-color: #0c8687;
  color: white;
  text-align: center;
  padding: 15px;
  border-radius: 5px;
  font-size: 24px;
  font-weight: bold;
}

/* Timer and Stop Timer */
.timer-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 20px 0;
}

#timer {
  font-size: 18px;
  color: #0c8687;
}

.stop-timer-button {
  background-color: #0c8687;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;
  max-width: 150px;
  transition: background-color 0.3s ease;
}

.stop-timer-button:hover {
  background-color: #086d68;
}

/* Create New Note Button */
.create-note {
  text-align: center;
  margin: 20px 0;
}

.create-note-button {
  background-color: #0c8687;
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;
}

.plus-symbol {
  font-size: 20px;
  margin-right: 10px;
}

.create-note-button:hover {
  background-color: #086d68;
}

/* Dropdown Sections */
.note-type-section,
.date-range-section {
  margin: 10px 0;
  text-align: center;
}

#noteTypeDropdown,
#noteCategoryDropdown,
#dateRangeDropdown,
.flatpickr-wrapper input {
  padding: 10px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 5px;
  width: 100%;
  max-width: 100%;
  text-align: left;
  box-sizing: border-box;
  color: #333;
  height: auto;
  background-color: #fff;
  transition: border-color 0.3s ease;
}

#noteTypeDropdown:focus,
#noteCategoryDropdown:focus,
#dateRangeDropdown:focus,
.flatpickr-wrapper input:focus {
  border-color: #0c8687;
  outline: none;
}

/* Date Button Styling */
.date-button {
  background-color: #0c8687;
  color: white;
  padding: 10px;
  border-radius: 5px;
  border: none;
  width: 100%;
  max-width: 100%;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  position: relative;
  transition: background-color 0.3s ease;
}

.date-button:hover {
  background-color: #086d68;
}

/* OR Text Styling */
.or-text {
  text-align: center;
  font-size: 16px;
  font-weight: bold;
  margin: 10px 0;
  color: #333;
}

/* Note Content Styling */
.note-content {
  background-color: #f9f9f9;
  padding: 0; /* Remove padding to manage overflow properly */
  border-radius: 5px;
  min-height: 30px;
  max-height: 600px;
  margin-bottom: 20px;
  position: relative;
  border: 1px solid #ccc;
  display: flex;
  flex-direction: column;
}

.note-content-inner {
  flex: 1;
  overflow-y: auto;
  padding: 15px;
}

.note-content-inner p {
  margin-bottom: 0;
}

.note-content-inner h4 {
  margin-bottom: 10px;
}

/* Note Tabs Styling */
.note-tabs-wrapper {
  /* overflow-x: auto; */
  white-space: nowrap;
  margin-bottom: 10px;
  padding-bottom: 10px;
}

.note-tabs {
  display: inline-flex;
  gap: 10px;
}

.tab {
  background-color: #cccccc;
  color: white;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  border: none;
  transition: background-color 0.3s ease, opacity 0.3s ease;
  flex: 0 0 auto; /* Prevent tabs from shrinking */
}

.inactive-tab {
  background-color: #cccccc;
  opacity: 0.7;
}

.active-tab {
  background-color: #0c8687;
  opacity: 1;
}

/* Pagination Fixed at Bottom */
.pagination {
  flex-shrink: 0;
  position: sticky;
  bottom: 0;
  background-color: #f9f9f9;
  padding: 10px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  z-index: 1;
  border-top: 1px solid #ccc;
}

.pagination button {
  background-color: #086d68;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.pagination button:hover:not(:disabled) {
  background-color: #064d4f;
}

.pagination button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

/* Custom Scrollbar Styling */
.note-content-inner::-webkit-scrollbar {
  width: 8px;
}

.note-content-inner::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.note-content-inner::-webkit-scrollbar-thumb {
  background: #0c8687;
  border-radius: 4px;
}

.note-content-inner::-webkit-scrollbar-thumb:hover {
  background: #086d68;
}

/* Prevent Content from Overlapping Tabs and Pagination */
.note-content-inner {
  padding-bottom: 60px; /* Space for pagination */
}

/* Media query for smaller screens */
@media only screen and (max-width: 600px) {
  .notes-container {
    margin: 8px 0;
    padding: 12px;
    /* Stay inside the viewport — no sideways swipe. */
    max-width: 100%;
    overflow-x: hidden;
  }

  .create-note-button,
  .stop-timer-button,
  #noteTypeDropdown,
  #noteCategoryDropdown,
  #dateRangeDropdown,
  .flatpickr-wrapper,
  .flatpickr-wrapper input {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .timer-section {
    gap: 8px;
    flex-wrap: wrap;
  }

  .note-content {
    min-height: 30px;
  }
}

/* Desktop Styles */

/* Reset and Global Styles */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.notes-container-desktop {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 30px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.notes-header-desktop {
  background-color: #0c8687;
  color: white;
  text-align: center;
  padding: 20px;
  border-radius: 5px;
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 25px;
}

.create-note-desktop {
  text-align: center;
  margin: 25px 0;
}

.create-note-button-desktop {
  background-color: #0c8687;
  color: white;
  border: none;
  padding: 15px 35px;
  border-radius: 5px;
  font-size: 18px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;
}

.plus-symbol-desktop {
  font-size: 24px;
  margin-right: 12px;
}

.create-note-button-desktop:hover {
  background-color: #086d68;
}

.timer-section-desktop {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 25px 0;
  padding: 0 20px;
}

#timer-desktop {
  font-size: 24px;
  color: #0c8687;
  font-weight: bold;
}

.stop-timer-button-desktop {
  background-color: #0c8687;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 5px;
  font-size: 18px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.stop-timer-button-desktop:hover {
  background-color: #086d68;
}

.dropdown-container-desktop {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin: 20px 0;
  padding: 0 20px;
}

.note-type-section-desktop {
  flex: 1;
}

.date-range-row {
  display: flex;
  align-items: center;
  gap: 10px; /* Adjusts spacing between elements */
  padding: 0 20px; /* Matches the padding in other sections */
}

.date-range-section-desktop {
  flex: 1; /* Ensures elements take equal space */
}

.or-text-desktop {
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

#noteTypeDropdownDesktop,
#noteCategoryDropdownDesktop,
#dateRangeDropdownDesktop,
.flatpickr-wrapper input {
  width: 100%;
  padding: 12px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 5px;
  background-color: #fff;
  transition: border-color 0.3s ease;
  cursor: pointer;
}

#noteTypeDropdownDesktop optgroup {
  font-weight: bold;
  color: #333;
  background-color: #f5f5f5;
  padding: 5px;
}

#noteTypeDropdownDesktop option {
  padding: 8px;
  background-color: #fff;
  color: #333;
}

#noteTypeDropdownDesktop option:not([value=""]):hover {
  background-color: #0c8687;
  color: white;
}

#noteTypeDropdownDesktop optgroup option {
  padding-left: 20px;
}

.or-text-desktop {
  text-align: center;
  font-size: 16px;
  font-weight: bold;
  margin: 20px 0;
  color: #333;
}

.notes-display-desktop {
  margin-top: 30px;
  padding: 0 20px;
}

.date-button-desktop {
  background-color: #0c8687;
  color: white;
  padding: 12px 20px;
  border-radius: 5px;
  border: none;
  width: 100%;
  margin-bottom: 12px;
  text-align: left;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.date-button-desktop:hover {
  background-color: #086d68;
}

.active-date-button-desktop {
  background-color: #086d68;
}

.note-content-desktop {
  background-color: #f9f9f9;
  padding: 20px;
  padding-bottom: 60px; /* Added padding to prevent content overlap */
  border-radius: 5px;
  min-height: 100px;
  max-height: 600px;
  overflow: hidden; /* Changed to hidden */
  margin-bottom: 20px;
  border: 1px solid #ccc;
  position: relative;
}

.note-content-inner-desktop {
  overflow-y: auto; /* Added overflow to this container */
  max-height: calc(
    100% - 60px
  ); /* Adjusted to account for pagination and resize handle */
}

.note-content-desktop h4 {
  font-size: 18px;
  margin-bottom: 10px;
  color: #333;
}

.note-content-desktop p {
  font-size: 16px;
  line-height: 1.6;
  color: #444;
  padding-right: 30px;
}

/* Tabs Styling */
.note-tabs-wrapper-desktop {
  /* overflow-x: auto; */
  white-space: nowrap;
  margin-bottom: 10px;
  padding-bottom: 10px;
}

.note-tabs-desktop {
  display: inline-flex;
  gap: 10px;
}

.tab-desktop {
  background-color: #cccccc;
  color: white;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  border: none;
  transition: all 0.3s ease;
  flex: 0 0 auto;
}

.inactive-tab-desktop {
  background-color: #cccccc;
  opacity: 0.7;
}

.active-tab-desktop {
  background-color: #0c8687;
  opacity: 1;
}

/* Pagination */
.pagination-desktop {
  position: absolute; /* Changed to absolute positioning */
  bottom: 35px; /* Adjusted to stay above the resize handle */
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
}

.pagination-desktop button {
  background-color: #086d68;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.pagination-desktop button:hover:not(:disabled) {
  background-color: #064d4f;
}

.pagination-desktop button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

/* Resize Handle */
.resize-handle-desktop {
  position: absolute;
  bottom: 5px;
  right: 5px;
  width: 24px;
  height: 24px;
  cursor: se-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s ease;
}

.resize-handle-desktop:hover {
  opacity: 0.8;
}

.resize-arrow {
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 0 12px 12px;
  border-color: transparent transparent #666 transparent;
  position: relative;
}

.resize-arrow::before {
  content: '';
  position: absolute;
  bottom: -12px;
  right: -12px;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 0 6px 6px;
  border-color: transparent transparent #ffffff transparent;
}

.resize-handle-desktop:hover .resize-arrow {
  border-color: transparent transparent #0c8687 transparent;
}

/* Scrollbar Styling */
.note-content-inner-desktop::-webkit-scrollbar,
.note-tabs-wrapper-desktop::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.note-content-inner-desktop::-webkit-scrollbar-track,
.note-tabs-wrapper-desktop::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.note-content-inner-desktop::-webkit-scrollbar-thumb,
.note-tabs-wrapper-desktop::-webkit-scrollbar-thumb {
  background: #0c8687;
  border-radius: 4px;
}

.note-content-inner-desktop::-webkit-scrollbar-thumb:hover,
.note-tabs-wrapper-desktop::-webkit-scrollbar-thumb:hover {
  background: #086d68;
}

@media only screen and (max-width: 576px) {
  .note__popup {
    flex-direction: column;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
    overflow-y: scroll;
    z-index: 1000;
    max-height: 700px;
    margin-top: 20%;
    box-sizing: border-box;
  }
}
</style>
